import * as React from 'react';
import autobind from 'autobind-decorator';
import { resolve } from 'inversify-react';
import { observable, computed } from 'mobx';
import { observer } from 'mobx-react';
import { Button, Modal } from 'antd';

import { ToastNotifications } from 'src/core/ui/toast';
import { ControllerProps } from 'src/router';
import { DialogService } from 'src/core/services/ui/dialog.service';
import { ObjectUtils } from 'src/core/utils/object';
import { ContextService } from 'src/core/services/context.service';
import { ClientFormView } from 'src/app/crm/clients/form/client-form.view';
import { AgentDataService } from 'src/app/bookings/agents/form/agent-form.data';
import { IdentityService } from 'src/core/services/identity.service';
import { FormatService } from 'src/core/services/format.service';
import { CurrencyListDataService } from 'src/app/general/currencies/list/currency-list.data';
import { AgentFormView } from 'src/app/bookings/agents/form/agent-form.view';
import { AgentBrandDataService } from 'src/app/bookings/agent-brands/agent-brand-form.data';
import { AgentPolicyDataService } from 'src/app/bookings/agent-policies/agent-policy-form.data';
import { AgentMarketDataService } from 'src/app/bookings/agent-markets/agent-market-form.data';

interface State {
	mode: ControlMode;
	currency: CurrencySummary;
}
interface QuoteAgentModalProps extends ComponentViewProps<QuoteItem> {
	type: string
	closeDialog: (ev?: React.MouseEvent<HTMLButtonElement>) => void;
	formatService: FormatService;
	showModal: boolean;
}

@observer
export class QuoteAgentModalController extends React.Component<QuoteAgentModalProps, State> {
	@observable
	protected dataService: AgentDataService;

	@resolve
	protected dialogService: DialogService;

	@resolve
	protected identityService: IdentityService;

	@resolve
	protected contextService: ContextService;

	@resolve
	protected toast: ToastNotifications;
	@observable
	brandDataService: AgentBrandDataService;

	@observable
	policyDataService: AgentPolicyDataService;
	@observable
	marketDataService: AgentMarketDataService;

	constructor(props) {
		super(props);
		this.state = { mode: 'edit', currency: null }
	}

	@computed
	protected get title() {
		if (this.dataService && this.dataService.item) {
			return `Agent ${this.dataService.item.name || ''}`;
		}
		return '';
	}

	handleModeChange = (mode: ControlMode): void => {
		this.setState({ mode });
	};


	@autobind
	addAgent() {
		this.props.setBusy(true);
		this.dataService.items = [];

		this.dataService.add(this.identityService.userId)
			.then(res => {
				this.props.setBusy(false);
			}).catch(error => {
				this.props.setBusy(false);
				this.toast.showToast('Request failed', ObjectUtils.parseError(error), 'error');
			});
	}
	handleChangeCurrency = (currency: CurrencySummary): void => {
		this.setState({ currency });
	};
	private async loadCurrency(currencyId: number): Promise<void> {
		const currencyListDataService = CurrencyListDataService.make(this.contextService);
		currencyListDataService.addFilter('id', 'eq', currencyId);
		currencyListDataService.addSorting('id', 'asc', true);
		currencyListDataService.pageSize = 1;

		await currencyListDataService.load(query => query);
		this.setState({
			currency: currencyListDataService.item
		});
	}
	@autobind
	public async save() {
		const item = this.dataService.item;
		const errors = await item.validate(true);
		if (errors.length == 0) {
			this.props.setBusy(true);
			item.brands = undefined;
			item.policies = undefined;
			item.markets = undefined;
			this.dataService.save()
				.then(res => {
					this.props.setBusy(false);
					const item = this.dataService.item;
					this.props.model.agentId = item.id;
					this.toast.showDataSaved();
					this.props.closeDialog();
				})
				.catch((error) => {
					this.props.setBusy(false);

					this.toast.showToast('Request failed', ObjectUtils.parseError(error), 'error');
					this.props.closeDialog();
				});
		} else {

			const err = errors.map(e => e.text);
			this.toast.showToast('Validation error!', err, 'error');
		}
	}

	@autobind
	public cancel() {
		return new Promise<boolean>((resolve, reject) => {
			if (this.dataService.isDirty) {
				this.dialogService.showYesNo('Warning', 'Are you sure want to close this form? Your changes will be lost!')
					.then(dialogResult => {
						if (dialogResult) {
							this.props.closeDialog();
						}
						resolve(true);
					})
					.catch(reject);
			} else {
				resolve(true);
			}
		});
	}

	public componentDidMount() {
		this.dataService = AgentDataService.make(this.contextService);
		this.brandDataService = AgentBrandDataService.make(this.contextService);
		this.policyDataService = AgentPolicyDataService.make(this.contextService);
		this.marketDataService = AgentMarketDataService.make(this.contextService)
		this.addAgent();

	}

	componentWillReceiveProps(nextProps: Readonly<QuoteAgentModalProps>, nextContext: any) {
		if (this.props.model.agentId != nextProps.model.agentId)
			this.addAgent();
	}


	render() {
		if (this.dataService == null || this.dataService.item == null) {
			return null;
		}

		return (
			<Modal
				className="ant-modalClient"
				visible={this.props.showModal}
				onCancel={this.cancel}
				title="Add Agent"
				maskClosable={false}
				width="80%"
				footer={[
					<Button key='save' onClick={this.save}>Save</Button>,
					<Button key='cancel' onClick={this.cancel}>Cancel</Button>
				]}>
				<AgentFormView
					match={null}
					location={null}
					history={null}
					brandDataService={this.brandDataService}
					policyDataService={this.policyDataService}
					model={this.dataService.item}
					title={this.title}
					isNew={true}
					cancel={this.cancel}
					save={this.save}
					contextService={this.contextService}
					mode={this.state.mode}
					onModeChange={this.handleModeChange}
					currency={this.state.currency}
					onChangeCurrency={this.handleChangeCurrency}
					offset={false}
					showHeader={false}
					className={false}
					setBusy={this.props.setBusy}
					marketDataService={this.marketDataService}
			/>
			</Modal>
		);
	}
}



// WEBPACK FOOTER //
// ./src/app/bookings/quotes/form/quote-agent-modal.controller.tsx